/**
 * ss-header.js
 * Web Component powering the "SS header" section (sections/ss-header.liquid)
 * for the Jarvis Technology theme.
 *
 * Everything is scoped to the <ss-header> element (body classes and the page
 * overlay are the only exceptions). All texts, timings and feature flags are
 * read from the JSON config rendered by Liquid:
 *   <script type="application/json" data-ss-header-config>
 *
 * Behaviours (see CONTRACT-HEADER.md "JS behaviours" 1-10):
 *   1. Hamburger            -> SsHeaderHamburger
 *   2. Mobile levels        -> SsHeaderLevels
 *   3. Desktop dropdowns    -> SsHeaderDesktopNav
 *   4. Mega menu            -> SsHeaderMegaMenu
 *   5. Banner slider        -> SsHeaderBannerSlider
 *   6. Sticky header        -> SsHeaderSticky
 *   7. Search               -> SsHeaderSearch
 *   8. Cart badge           -> SsHeaderCart
 *   9. Tracking             -> SsHeaderTracking (+ track() guard)
 *  10. Strings via config   -> createTranslator / ssT
 */
(function () {
  'use strict';

  /* The theme declares `const PUB_SUB_EVENTS` in pubsub.js — a lexical global,
     not a window property — so resolve it lazily by identifier first. */
  function ssPubSubEvents() {
    try { if (typeof PUB_SUB_EVENTS !== 'undefined') return PUB_SUB_EVENTS; } catch (e) { /* noop */ }
    return ssPubSubEvents() || null;
  }
  function ssPublish(eventName, payload) {
    try { if (typeof publish === 'function') return publish(eventName, payload); } catch (e) { /* noop */ }
    if (typeof window.publish === 'function') return ssPublish(eventName, payload);
    return undefined;
  }
  function ssSubscribe(eventName, callback) {
    try { if (typeof subscribe === 'function') return subscribe(eventName, callback); } catch (e) { /* noop */ }
    if (typeof window.subscribe === 'function') return ssSubscribe(eventName, callback);
    return null;
  }

  if (window.customElements && customElements.get('ss-header')) return;

  var LOG_PREFIX = '[ss-header]';
  var DESKTOP_MIN_WIDTH = 990;
  var DESKTOP_LEAVE_DELAY_MS = 150;
  var MEGA_HIDE_DELAY_MS = 250;
  var SEARCH_CYCLE_MS = 2000;
  var BANNER_INTERVAL_MS = 4000;
  var TRACK_THROTTLE_MS = 300;
  var SCROLL_CLOSE_DELTA_PX = 5;
  var SCROLLED_PX = 4;

  var CLASSES = {
    menuOpen: 'is-menu-open',
    levelOpen: 'is-level-open',
    dropdownOpen: 'is-dropdown-open',
    megaOpen: 'is-mega-open',
    searchOpen: 'is-search-open',
    sticky: 'is-sticky',
    scrolled: 'is-scrolled',
    hidden: 'is-hidden',
    desktop: 'is-desktop',
    mobile: 'is-mobile',
    itemOpen: 'is-open',
    itemHover: 'is-hover',
    megaActive: 'active',
    megaTabActive: 'active-item',
    overlay: 'ss-header__overlay',
    overlayVisible: 'is-visible',
    srOnly: 'ss-header__sr-only',
    badge: 'ss-header__badge'
  };

  var BODY_CLASSES = {
    menuOpen: 'ss-header-menu-open',
    searchOpen: 'ss-header-search-open',
    stickyActive: 'ss-header-sticky-active',
    overlayOpen: 'ss-header-overlay-open'
  };

  function warn() {
    var args = Array.prototype.slice.call(arguments);
    args.unshift(LOG_PREFIX);
    try { console.warn.apply(console, args); } catch (e) { /* noop */ }
  }

  /* ------------------------------------------------------------------ */
  /* Generic helpers                                                     */
  /* ------------------------------------------------------------------ */

  function isObject(v) { return v !== null && typeof v === 'object'; }

  function get(obj, path, fallback) {
    var parts = String(path).split('.');
    var cur = obj;
    for (var i = 0; i < parts.length; i++) {
      if (!isObject(cur) || !(parts[i] in cur)) return fallback;
      cur = cur[parts[i]];
    }
    return cur === undefined || cur === null ? fallback : cur;
  }

  function toNumber(value, fallback) {
    var n = Number(value);
    return Number.isFinite(n) ? n : (fallback === undefined ? 0 : fallback);
  }

  function cleanText(value) {
    return String(value === undefined || value === null ? '' : value).replace(/\s+/g, ' ').trim();
  }

  function normalizeKey(value) {
    return cleanText(value).toLowerCase();
  }

  function splitList(value) {
    return String(value || '').split(/[,|]/).map(function (s) { return cleanText(s); }).filter(Boolean);
  }

  function show(el, visible) {
    if (!el) return;
    if (visible === false) {
      el.setAttribute('hidden', '');
    } else {
      el.removeAttribute('hidden');
      if (el.style && el.style.display === 'none') el.style.display = '';
    }
  }

  function hide(el) { show(el, false); }

  function isVisible(el) {
    return !!el && !el.hasAttribute('hidden') && !el.classList.contains('hidden');
  }

  function prefersReducedMotion() {
    try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (e) { return false; }
  }

  function debounce(fn, wait) {
    var t;
    return function () {
      var args = arguments, ctx = this;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }

  function parseJsonScript(scriptEl, fallback) {
    if (!scriptEl) return fallback;
    try {
      var text = (scriptEl.textContent || '').trim();
      return text ? JSON.parse(text) : fallback;
    } catch (e) {
      warn('Invalid JSON in', scriptEl.getAttribute('type'), e);
      return fallback;
    }
  }

  function parseJsonAttr(value, fallback) {
    var text = cleanText(value);
    if (!text) return fallback;
    try { return JSON.parse(text); } catch (e) { return fallback; }
  }

  function closestWithin(target, selector, boundary) {
    if (!target || typeof target.closest !== 'function') return null;
    var el = target.closest(selector);
    if (!el) return null;
    if (boundary && !boundary.contains(el)) return null;
    return el;
  }

  /* ------------------------------------------------------------------ */
  /* Strings: ssT(key, vars)                                             */
  /* ------------------------------------------------------------------ */

  /* Shopify's `t` filter HTML-escapes translations; decode exactly the five entities it emits. */
  function ssUnescape(text) {
    if (typeof text !== 'string' || text.indexOf('&') === -1) return text;
    return text.replace(/&(#39|quot|lt|gt|amp);/g, function (m, e) {
      return { '#39': "'", quot: '"', lt: '<', gt: '>', amp: '&' }[e];
    });
  }

  function createTranslator(strings) {
    var dict = isObject(strings) ? strings : {};
    return function ssT(key, vars) {
      var text = Object.prototype.hasOwnProperty.call(dict, key) && typeof dict[key] === 'string' ? ssUnescape(dict[key]) : key;
      if (vars && isObject(vars)) {
        text = text.replace(/\{\{\s*([\w.]+)\s*\}\}/g, function (m, name) {
          return Object.prototype.hasOwnProperty.call(vars, name) ? String(vars[name]) : m;
        });
      }
      return text;
    };
  }

  /* ------------------------------------------------------------------ */
  /* Analytics                                                           */
  /* ------------------------------------------------------------------ */

  function createTracker(config) {
    var enabled = get(config, 'analytics', true) !== false && get(config, 'features.analytics', true) !== false;
    return function track(eventName, params) {
      if (!enabled || !eventName) return;
      var p = isObject(params) ? params : {};
      try {
        if (typeof window.gaEvent === 'function') {
          var legacy = Array.isArray(p.legacy) ? p.legacy.slice(0, 6) : [];
          while (legacy.length < 6) legacy.push('');
          window.gaEvent(eventName, legacy, false);
        }
      } catch (e) { /* noop */ }
      try {
        if (Array.isArray(window.dataLayer)) {
          var payload = Object.assign({ event: eventName }, p);
          delete payload.legacy;
          // Port of pushToDataLayer(): clear the previous "custom" params before pushing new ones.
          if (payload.custom) window.dataLayer.push({ custom: null });
          window.dataLayer.push(payload);
        }
      } catch (e) { /* noop */ }
      try {
        if (window.webengage && typeof window.webengage.track === 'function') {
          var we = Object.assign({}, p);
          delete we.legacy;
          window.webengage.track(eventName, we);
        }
      } catch (e) { /* noop */ }
    };
  }

  /** Legacy pushToDataLayer(eventName, [p1..p6]) shape -> track() params ({ legacy, custom: { param_1..6 } }). */
  function legacyParams(list, extra) {
    var arr = Array.isArray(list) ? list.slice(0, 6) : [];
    while (arr.length < 6) arr.push('');
    var custom = {};
    for (var i = 0; i < 6; i++) custom['param_' + (i + 1)] = arr[i] || '';
    return Object.assign({ legacy: arr, custom: custom }, extra || {});
  }

  /* ------------------------------------------------------------------ */
  /* Focus management: layer stack (ESC / focus trap / return focus)     */
  /* ------------------------------------------------------------------ */

  var FOCUSABLE = 'summary, a[href], button:not([disabled]), [tabindex]:not([tabindex^="-"]), input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), iframe';

  function getFocusable(container) {
    if (!container) return [];
    return Array.from(container.querySelectorAll(FOCUSABLE)).filter(function (el) {
      if (el.closest('[hidden]')) return false;
      return el.offsetParent !== null || el === document.activeElement || getComputedStyle(el).position === 'fixed';
    });
  }

  /**
   * Keeps track of open overlay layers. ESC closes only the topmost layer.
   * Each layer traps Tab focus inside its container and returns focus on close.
   */
  function createLayerStack() {
    var layers = [];
    var bound = false;

    function onKeydown(event) {
      if (!layers.length) return;
      var top = layers[layers.length - 1];
      if (event.key === 'Escape' || event.key === 'Esc') {
        event.preventDefault();
        event.stopPropagation();
        if (typeof top.onClose === 'function') top.onClose();
        return;
      }
      if (event.key !== 'Tab' || !top.container) return;
      if (top.container.contains(document.activeElement)) {
        var focusable = getFocusable(top.container);
        if (!focusable.length) { event.preventDefault(); return; }
        var first = focusable[0], last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
      } else {
        var items = getFocusable(top.container);
        if (items.length) { event.preventDefault(); items[0].focus(); }
      }
    }

    function bind() {
      if (bound) return;
      document.addEventListener('keydown', onKeydown, true);
      bound = true;
    }
    function unbind() {
      if (!bound) return;
      document.removeEventListener('keydown', onKeydown, true);
      bound = false;
    }

    return {
      push: function (layer) {
        if (!layer || !layer.container) return;
        this.remove(layer.container, true);
        layer.returnFocus = layer.returnFocus || document.activeElement;
        layers.push(layer);
        bind();
        var target = layer.initialFocus || getFocusable(layer.container)[0] || layer.container;
        try {
          if (!layer.container.hasAttribute('tabindex') && target === layer.container) layer.container.setAttribute('tabindex', '-1');
          target.focus({ preventScroll: true });
        } catch (e) { /* noop */ }
      },
      remove: function (container, skipReturnFocus) {
        var idx = -1;
        for (var i = layers.length - 1; i >= 0; i--) { if (layers[i].container === container) { idx = i; break; } }
        if (idx === -1) return;
        var layer = layers.splice(idx, 1)[0];
        if (!layers.length) unbind();
        if (skipReturnFocus) return;
        try {
          if (layer.returnFocus && typeof layer.returnFocus.focus === 'function' && document.contains(layer.returnFocus)) {
            layer.returnFocus.focus({ preventScroll: true });
          }
        } catch (e) { /* noop */ }
      },
      has: function (container) {
        return layers.some(function (l) { return l.container === container; });
      },
      isTop: function (container) {
        return layers.length > 0 && layers[layers.length - 1].container === container;
      },
      clear: function () {
        while (layers.length) {
          var l = layers.pop();
          try { if (typeof l.onClose === 'function') l.onClose(true); } catch (e) { /* noop */ }
        }
        unbind();
      }
    };
  }

  /* ------------------------------------------------------------------ */
  /* Roving focus inside a container (ArrowUp/Down, Home/End)            */
  /* ------------------------------------------------------------------ */

  function moveFocus(container, event, opts) {
    var options = opts || {};
    var items = getFocusable(container).filter(function (el) { return !options.exclude || !options.exclude(el); });
    if (!items.length) return false;
    var idx = items.indexOf(document.activeElement);
    var next = null;
    switch (event.key) {
      case 'ArrowDown':
      case 'Down':
        next = idx === -1 ? items[0] : items[Math.min(items.length - 1, idx + 1)];
        if (idx === items.length - 1 && options.loop) next = items[0];
        break;
      case 'ArrowUp':
      case 'Up':
        next = idx === -1 ? items[items.length - 1] : items[Math.max(0, idx - 1)];
        if (idx === 0 && options.loop) next = items[items.length - 1];
        break;
      case 'Home':
        next = items[0];
        break;
      case 'End':
        next = items[items.length - 1];
        break;
      default:
        return false;
    }
    if (!next) return false;
    event.preventDefault();
    try { next.focus(); } catch (e) { /* noop */ }
    return true;
  }

  function isActivationKey(event) {
    return event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar';
  }

  /* ------------------------------------------------------------------ */
  /* Module base                                                         */
  /* ------------------------------------------------------------------ */

  class SsHeaderModule {
    constructor(root) {
      this.root = root;
      this.config = root.config;
      this.t = root.t;
      this.track = root.track;
      this.timers = [];
      this.offs = [];
    }
    $(selector) { return this.root.querySelector(selector); }
    $$(selector) { return Array.from(this.root.querySelectorAll(selector)); }
    /** Registers a listener on the root (cleaned up on destroy) and remembers the remover for mode re-binding. */
    on(el, evt, fn, opts) {
      var off = this.root.on(el, evt, fn, opts);
      this.offs.push(off);
      return off;
    }
    offAll() {
      this.offs.forEach(function (off) { try { off(); } catch (e) { /* noop */ } });
      this.offs = [];
    }
    setTimeout(fn, ms) {
      var id = window.setTimeout(fn, ms);
      this.timers.push({ type: 't', id: id });
      return id;
    }
    setInterval(fn, ms) {
      var id = window.setInterval(fn, ms);
      this.timers.push({ type: 'i', id: id });
      return id;
    }
    clearTimer(id) {
      this.timers = this.timers.filter(function (t) {
        if (t.id !== id) return true;
        t.type === 'i' ? clearInterval(t.id) : clearTimeout(t.id);
        return false;
      });
    }
    clearTimers() {
      this.timers.forEach(function (t) { t.type === 'i' ? clearInterval(t.id) : clearTimeout(t.id); });
      this.timers = [];
    }
    get header() { return this.root.headerEl; }
    isDesktop() { return this.root.isDesktop(); }
    init() {}
    /** Called when the desktop/mobile media query flips. */
    onBreakpoint() {}
    /** Called by root.closeAll(reason). */
    closeAll() {}
    destroy() { this.clearTimers(); this.offAll(); }
  }

  /* ------------------------------------------------------------------ */
  /* 1. Hamburger (mobile full-screen panel)                             */
  /* ------------------------------------------------------------------ */

  class SsHeaderHamburger extends SsHeaderModule {
    init() {
      this.button = this.$('[data-ss-hamburger]') || this.$('.header-hamburger-icon');
      this.panel = this.$('[data-ss-mobile-menu]') || this.$('.fs-main-container');
      if (!this.button || !this.panel) return;
      // The nav tree (.mobile-navigation-v1) is a sibling of the panel inside this wrapper: trap focus on the wrapper.
      this.container = this.panel.closest('.header-wrapper-common-main-prnt-absolute') || this.panel;
      this.isOpen = false;

      if (!this.panel.id) this.panel.id = 'SsHeaderMobileMenu-' + (this.root.sectionId || 'default');
      if (!this.button.hasAttribute('aria-controls')) this.button.setAttribute('aria-controls', this.panel.id);
      this.button.setAttribute('aria-expanded', 'false');
      this.updateButtonLabel();
      hide(this.panel);

      this.on(this.button, 'click', (event) => {
        event.preventDefault();
        if (this.root.classList.contains(CLASSES.levelOpen) && this.root.levels) {
          this.root.levels.back();
          return;
        }
        this.toggle();
      });

      this.on(this.root, 'ss-header:level', () => this.updateButtonLabel());
    }

    updateButtonLabel() {
      if (!this.button) return;
      var key = 'openMenu';
      if (this.root.classList.contains(CLASSES.levelOpen)) key = 'backToMenu';
      else if (this.isOpen) key = 'closeMenu';
      this.button.setAttribute('aria-label', this.t(key));
    }

    toggle() { this.isOpen ? this.close() : this.open(); }

    open() {
      if (!this.panel || this.isOpen) return;
      this.isOpen = true;
      this.root.closeAll('hamburger');
      show(this.panel);
      this.root.classList.add(CLASSES.menuOpen);
      this.button.setAttribute('aria-expanded', 'true');
      document.body.classList.add(BODY_CLASSES.menuOpen);
      this.root.showOverlay('menu');
      this.panel.scrollTop = 0;
      this.container.scrollTop = 0;
      this.root.layers.push({
        container: this.container,
        initialFocus: getFocusable(this.panel)[0] || getFocusable(this.container)[0] || this.panel,
        returnFocus: this.button,
        onClose: () => this.close()
      });
      this.updateButtonLabel();
      this.track('hamburger_open', legacyParams(['HB_open']));
      this.root.dispatchEvent(new CustomEvent('ss-header:menu', { detail: { open: true } }));
    }

    close(silent) {
      if (!this.panel || !this.isOpen) return;
      this.isOpen = false;
      if (this.root.levels) this.root.levels.reset();
      hide(this.panel);
      this.root.classList.remove(CLASSES.menuOpen, CLASSES.levelOpen);
      this.button.setAttribute('aria-expanded', 'false');
      document.body.classList.remove(BODY_CLASSES.menuOpen);
      this.root.hideOverlay('menu');
      this.root.layers.remove(this.container, silent === true);
      this.updateButtonLabel();
      this.root.dispatchEvent(new CustomEvent('ss-header:menu', { detail: { open: false } }));
    }

    onBreakpoint(desktop) {
      if (desktop) this.close(true);
    }

    closeAll(reason) {
      if (reason === 'overlay' || reason === 'search' || reason === 'editor' || reason === 'breakpoint') this.close(reason === 'breakpoint');
    }

    destroy() {
      this.close(true);
      super.destroy();
    }
  }

  /* ------------------------------------------------------------------ */
  /* 2. Mobile levels (3-layer navigation + category grid)               */
  /* ------------------------------------------------------------------ */

  class SsHeaderLevels extends SsHeaderModule {
    init() {
      this.nav = this.$('[data-ss-nav]') || this.$('.mobile-navigation-v1 nav.header-nav') || this.$('nav.header-nav');
      this.panel = this.$('[data-ss-mobile-menu]') || this.$('.fs-main-container');
      this.container = this.panel ? (this.panel.closest('.header-wrapper-common-main-prnt-absolute') || this.panel) : null;
      if (!this.nav) return;
      this.openItem = null;

      // Level-1 toggles rendered as <div>: make them operable.
      this.$$('.nav-link-title.dev_click_open_level2_mobile', this.nav).forEach((toggle) => {
        var item = toggle.closest('.menu-item');
        var panel = item ? item.querySelector('.prnt-div-whole') : null;
        if (!panel) return;
        if (toggle.tagName !== 'BUTTON' && toggle.tagName !== 'A') {
          toggle.setAttribute('role', 'button');
          if (!toggle.hasAttribute('tabindex')) toggle.setAttribute('tabindex', '0');
        }
        toggle.setAttribute('aria-expanded', 'false');
        if (!panel.id) panel.id = 'SsHeaderLevel2-' + (this.root.sectionId || 'x') + '-' + Math.random().toString(36).slice(2, 8);
        toggle.setAttribute('aria-controls', panel.id);
      });

      // Level-2 group toggles (<p> in the legacy markup): operable buttons on mobile, plain headings on desktop.
      // NB: browsers hoist a <div class="no-anchor-div"> out of its <p> parent, so the label may be a sibling of the <p>.
      this.groupLabels = [];
      this.$$('.submenu-item.dev_click_toggle_level3_hlink, .submenu-item.custom-dropdown', this.nav).forEach((group) => {
        var list = group.querySelector('.prnt-level3');
        var toggle = group.querySelector('.toggle-level-2');
        if (!list || !toggle) return;
        var label = this.groupLabel(group);
        if (label && label.tagName !== 'A' && this.groupLabels.indexOf(label) === -1) {
          label.setAttribute('data-ss-label', this.t('submenuToggle', { title: cleanText(toggle.getAttribute('title') || label.textContent) }));
          this.groupLabels.push(label);
        }
      });
      this.applyMode(this.isDesktop());

      this.on(this.nav, 'click', (event) => this.onClick(event));
      this.on(this.nav, 'keydown', (event) => this.onKeydown(event));

      // Category grid -> matching level-1 item.
      var grid = this.$('.fs-categories-wrapper');
      if (grid) {
        this.on(grid, 'click', (event) => {
          var cat = closestWithin(event.target, '.fs-category-item[data-target]', grid);
          if (!cat) return;
          event.preventDefault();
          var target = cat.getAttribute('data-target');
          var opened = this.openByTitle(target);
          this.track('hamburger_category', legacyParams(['HB_category', cleanText(target), cat.getAttribute('data-catindex') || ''], { category: cleanText(target), found: opened }));
        });
      }
    }

    $$(selector, scope) { return Array.from((scope || this.root).querySelectorAll(selector)); }

    /** The operable heading of a level-2 group: `.no-anchor-div` (inside or next to the <p>), else the <p> itself. */
    groupLabel(group) {
      if (!group) return null;
      var outsideList = function (el) { return !el.closest('.prnt-level3'); };
      var pick = function (selector) { return Array.from(group.querySelectorAll(selector)).find(outsideList) || null; };
      // Priority: the text carrier (.no-anchor-div), then an anchor heading, then the <p> itself.
      return pick('.no-anchor-div') || pick('.toggle-level-2 > a') || pick('.toggle-level-2');
    }

    /** True when the click landed on the group's heading area (not on a level-3 link). */
    isGroupHeading(target, group) {
      if (!group || !group.contains(target)) return false;
      var list = group.querySelector('.prnt-level3');
      if (list && list.contains(target)) return false;
      return !!(target.closest('.toggle-level-2') || target.closest('.no-anchor-div') || target.closest('svg') || target === group);
    }

    applyMode(desktop) {
      (this.groupLabels || []).forEach((label) => {
        if (desktop) {
          label.removeAttribute('role');
          label.removeAttribute('tabindex');
          label.removeAttribute('aria-expanded');
          label.removeAttribute('aria-label');
        } else {
          label.setAttribute('role', 'button');
          label.setAttribute('tabindex', '0');
          label.setAttribute('aria-expanded', label.closest('.submenu-item') && label.closest('.submenu-item').classList.contains(CLASSES.itemOpen) ? 'true' : 'false');
          label.setAttribute('aria-label', label.getAttribute('data-ss-label') || '');
        }
      });
    }

    onClick(event) {
      if (this.isDesktop()) return;
      var l1 = closestWithin(event.target, '.dev_click_open_level2_mobile', this.nav);
      if (l1) {
        var item = l1.closest('.menu-item');
        if (item && item.querySelector('.prnt-div-whole')) {
          event.preventDefault();
          this.openLevel(item);
        }
        return;
      }
      var group = closestWithin(event.target, '.submenu-item', this.nav);
      if (group && group.querySelector('.prnt-level3') && this.isGroupHeading(event.target, group)) {
        // Group headings may be anchors on desktop; on mobile they act as accordion toggles.
        event.preventDefault();
        this.toggleGroup(group);
      }
    }

    onKeydown(event) {
      if (this.isDesktop()) return;
      if (!isActivationKey(event)) return;
      var target = event.target;
      if (!target || target.tagName === 'A' || target.tagName === 'BUTTON') return;
      var group = closestWithin(target, '.submenu-item', this.nav);
      if (target.closest('.dev_click_open_level2_mobile') || (group && this.isGroupHeading(target, group))) {
        event.preventDefault();
        target.click();
      }
    }

    openLevel(item) {
      if (!item) return;
      if (this.openItem && this.openItem !== item) this.closeLevel(this.openItem, true);
      this.openItem = item;
      item.classList.add(CLASSES.itemOpen);
      var toggle = item.querySelector('.dev_click_open_level2_mobile');
      if (toggle) toggle.setAttribute('aria-expanded', 'true');
      this.root.classList.add(CLASSES.levelOpen);
      if (this.panel) this.panel.scrollTop = 0;
      if (this.container) this.container.scrollTop = 0;
      var panel = item.querySelector('.prnt-div-whole');
      if (panel) {
        panel.scrollTop = 0;
        var first = getFocusable(panel)[0];
        if (first) { try { first.focus({ preventScroll: true }); } catch (e) { /* noop */ } }
      }
      this.root.dispatchEvent(new CustomEvent('ss-header:level', { detail: { level: 2, item: item } }));
      this.track('hamburger_level2', legacyParams(['HB_level2', cleanText(toggle ? toggle.textContent : '')]));
    }

    closeLevel(item, keepFocus) {
      if (!item) return;
      item.classList.remove(CLASSES.itemOpen);
      var toggle = item.querySelector('.dev_click_open_level2_mobile');
      if (toggle) toggle.setAttribute('aria-expanded', 'false');
      Array.from(item.querySelectorAll('.submenu-item.' + CLASSES.itemOpen)).forEach((g) => this.setGroup(g, false));
      if (this.openItem === item) this.openItem = null;
      if (!keepFocus && toggle) { try { toggle.focus({ preventScroll: true }); } catch (e) { /* noop */ } }
    }

    back() {
      var item = this.openItem || this.nav.querySelector('.menu-item.' + CLASSES.itemOpen);
      if (item) this.closeLevel(item, false);
      this.root.classList.remove(CLASSES.levelOpen);
      if (this.panel) this.panel.scrollTop = 0;
      if (this.container) this.container.scrollTop = 0;
      this.root.dispatchEvent(new CustomEvent('ss-header:level', { detail: { level: 1 } }));
    }

    toggleGroup(group) {
      var open = !group.classList.contains(CLASSES.itemOpen);
      var parent = group.parentElement;
      if (open && parent) {
        Array.from(parent.querySelectorAll(':scope > .submenu-item.' + CLASSES.itemOpen)).forEach((g) => { if (g !== group) this.setGroup(g, false); });
      }
      this.setGroup(group, open);
    }

    setGroup(group, open) {
      group.classList.toggle(CLASSES.itemOpen, open);
      var label = this.groupLabel(group);
      if (label && label.hasAttribute('aria-expanded')) label.setAttribute('aria-expanded', open ? 'true' : 'false');
    }

    /** Finds the level-1 item by title (data-id / data-parentdropdowntitle), tolerant to plural "s". */
    findItemByTitle(title) {
      var key = normalizeKey(title);
      if (!key || !this.nav) return null;
      var candidates = Array.from(this.nav.querySelectorAll('.dev_click_toggle_level2_hlink[data-id], .nav-link-title[data-parentdropdowntitle], .menu-item[data-ss-menu-item][data-title]'));
      var score = function (el) {
        var v = normalizeKey(el.getAttribute('data-id') || el.getAttribute('data-parentdropdowntitle') || el.getAttribute('data-title'));
        if (!v) return 0;
        if (v === key) return 3;
        if (v.replace(/s$/, '') === key.replace(/s$/, '')) return 2;
        if (v.indexOf(key) === 0 || key.indexOf(v) === 0) return 1;
        return 0;
      };
      var best = null, bestScore = 0;
      candidates.forEach(function (el) {
        var s = score(el);
        if (s > bestScore) { bestScore = s; best = el; }
      });
      return best ? best.closest('.menu-item') : null;
    }

    openByTitle(title) {
      var item = this.findItemByTitle(title);
      if (!item) return false;
      if (!item.querySelector('.prnt-div-whole')) {
        var link = item.querySelector('a[href]');
        if (link) { try { link.click(); } catch (e) { /* noop */ } }
        return !!link;
      }
      this.openLevel(item);
      return true;
    }

    reset() {
      if (!this.nav) return;
      Array.from(this.nav.querySelectorAll('.menu-item.' + CLASSES.itemOpen)).forEach((item) => this.closeLevel(item, true));
      Array.from(this.nav.querySelectorAll('.submenu-item.' + CLASSES.itemOpen)).forEach((g) => this.setGroup(g, false));
      this.openItem = null;
      this.root.classList.remove(CLASSES.levelOpen);
    }

    onBreakpoint(desktop) {
      if (desktop) this.reset();
      this.applyMode(desktop);
    }

    destroy() {
      this.reset();
      this.applyMode(true);
      super.destroy();
    }
  }

  /* ------------------------------------------------------------------ */
  /* 3. Desktop dropdowns (hover / focus / keyboard)                     */
  /* ------------------------------------------------------------------ */

  class SsHeaderDesktopNav extends SsHeaderModule {
    init() {
      this.nav = this.$('[data-ss-nav]') || this.$('.mobile-navigation-v1 nav.header-nav') || this.$('nav.header-nav');
      if (!this.nav) return;
      this.items = Array.from(this.nav.querySelectorAll('.menu-item.custom-dropdown')).filter(function (item) {
        return !!item.querySelector('.prnt-div-whole');
      });
      this.openItem = null;
      this.leaveTimer = null;
      this.modeOffs = [];
      if (this.isDesktop()) this.enable();
    }

    toggleOf(item) { return item.querySelector('.nav-link-title') || item.querySelector('.toggle-level-1'); }
    panelOf(item) { return item.querySelector('.prnt-div-whole'); }

    bindMode(el, evt, fn, opts) {
      var off = this.on(el, evt, fn, opts);
      this.modeOffs.push(off);
      return off;
    }

    enable() {
      if (this.enabled || !this.items.length) return;
      this.enabled = true;
      this.items.forEach((item) => {
        var toggle = this.toggleOf(item);
        if (toggle) {
          toggle.setAttribute('aria-haspopup', 'true');
          toggle.setAttribute('aria-expanded', 'false');
        }
        this.bindMode(item, 'mouseenter', () => { this.cancelLeave(); this.open(item); });
        this.bindMode(item, 'mouseleave', () => this.scheduleClose(item));
        this.bindMode(item, 'focusin', () => { this.cancelLeave(); this.open(item); });
        this.bindMode(item, 'focusout', (event) => {
          if (event.relatedTarget && item.contains(event.relatedTarget)) return;
          this.scheduleClose(item);
        });
        this.bindMode(item, 'keydown', (event) => this.onKeydown(event, item));
        if (toggle) {
          this.bindMode(toggle, 'click', (event) => {
            // Touch devices at desktop width: first tap opens, second navigates (anchor) / closes (div).
            if (toggle.tagName === 'A' && item.classList.contains(CLASSES.itemHover)) return;
            if (toggle.tagName === 'A' && !item.classList.contains(CLASSES.itemHover) && this.root.isTouch) {
              event.preventDefault();
              this.open(item);
              return;
            }
            if (toggle.tagName !== 'A') {
              event.preventDefault();
              item.classList.contains(CLASSES.itemHover) ? this.close(item) : this.open(item);
            }
          });
        }
      });
      this.bindMode(document, 'click', (event) => {
        if (!this.openItem) return;
        if (this.root.contains(event.target)) return;
        this.closeAll('outside');
      });
    }

    disable() {
      if (!this.enabled) return;
      this.enabled = false;
      this.closeAll('breakpoint');
      this.modeOffs.forEach(function (off) { try { off(); } catch (e) { /* noop */ } });
      this.modeOffs = [];
      this.items.forEach((item) => {
        var toggle = this.toggleOf(item);
        if (toggle) { toggle.removeAttribute('aria-haspopup'); }
      });
    }

    cancelLeave() {
      if (this.leaveTimer !== null) { this.clearTimer(this.leaveTimer); this.leaveTimer = null; }
    }

    scheduleClose(item) {
      this.cancelLeave();
      this.leaveTimer = this.setTimeout(() => {
        this.leaveTimer = null;
        if (item.matches(':hover') || item.contains(document.activeElement)) return;
        this.close(item);
      }, DESKTOP_LEAVE_DELAY_MS);
    }

    open(item) {
      if (!this.enabled || !item) return;
      if (this.openItem && this.openItem !== item) this.close(this.openItem);
      if (this.openItem === item) return;
      this.root.closeAll('dropdown');
      this.openItem = item;
      item.classList.add(CLASSES.itemHover);
      var toggle = this.toggleOf(item);
      if (toggle) toggle.setAttribute('aria-expanded', 'true');
      this.root.classList.add(CLASSES.dropdownOpen);
    }

    close(item) {
      if (!item) return;
      item.classList.remove(CLASSES.itemHover);
      var toggle = this.toggleOf(item);
      if (toggle) toggle.setAttribute('aria-expanded', 'false');
      if (this.openItem === item) this.openItem = null;
      if (!this.openItem) this.root.classList.remove(CLASSES.dropdownOpen);
    }

    closeAll(reason) {
      if (reason === 'dropdown') return;
      this.cancelLeave();
      this.items.forEach((item) => { if (item.classList.contains(CLASSES.itemHover)) this.close(item); });
      this.openItem = null;
      this.root.classList.remove(CLASSES.dropdownOpen);
    }

    onKeydown(event, item) {
      var toggle = this.toggleOf(item);
      var panel = this.panelOf(item);
      var onToggle = toggle && (event.target === toggle || toggle.contains(event.target));
      var inPanel = panel && panel.contains(event.target);

      if (event.key === 'Escape' || event.key === 'Esc') {
        if (!item.classList.contains(CLASSES.itemHover)) return;
        event.preventDefault();
        event.stopPropagation();
        this.close(item);
        if (toggle) { try { toggle.focus(); } catch (e) { /* noop */ } }
        return;
      }

      if (onToggle) {
        if (isActivationKey(event) && toggle.tagName !== 'A') {
          event.preventDefault();
          item.classList.contains(CLASSES.itemHover) ? this.close(item) : this.open(item);
          return;
        }
        if (event.key === 'ArrowDown' || event.key === 'Down') {
          event.preventDefault();
          this.open(item);
          var first = panel ? getFocusable(panel)[0] : null;
          if (first) { try { first.focus(); } catch (e) { /* noop */ } }
          return;
        }
        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft' || event.key === 'Right' || event.key === 'Left') {
          var dir = (event.key === 'ArrowRight' || event.key === 'Right') ? 1 : -1;
          if (this.root.isRtl) dir = -dir;
          var idx = this.items.indexOf(item);
          var next = this.items[(idx + dir + this.items.length) % this.items.length];
          var nextToggle = next ? this.toggleOf(next) : null;
          if (nextToggle) {
            event.preventDefault();
            this.close(item);
            try { nextToggle.focus(); } catch (e) { /* noop */ }
          }
          return;
        }
        return;
      }

      if (inPanel) {
        moveFocus(panel, event, { loop: true });
      }
    }

    onBreakpoint(desktop) {
      desktop ? this.enable() : this.disable();
    }

    destroy() {
      this.disable();
      super.destroy();
    }
  }

  /* ------------------------------------------------------------------ */
  /* 4. Mega menu (tabs -> sidebar -> server-rendered panels)            */
  /* ------------------------------------------------------------------ */

  class SsHeaderMegaMenu extends SsHeaderModule {
    init() {
      this.wrapper = this.$('[data-ss-megamenu]') || this.$('.tsc-megamenu-header');
      if (!this.wrapper) return;
      this.mega = this.wrapper.querySelector('.tsc-megamenu-mega-wrapper');
      this.tabs = Array.from(this.wrapper.querySelectorAll('.tsc-megamenu-nav-item[data-menu]'));
      this.sidebar = this.wrapper.querySelector('.tsc-megamenu-sidebar');
      this.sidebarList = this.sidebar ? (this.sidebar.querySelector('ul') || this.sidebar) : null;
      this.sidebarItems = this.sidebarList ? Array.from(this.sidebarList.querySelectorAll('li[data-item]')) : [];
      this.panels = Array.from(this.wrapper.querySelectorAll('.tsc-megamenu-panel[data-panel]'));
      if (!this.mega || !this.tabs.length) return;

      this.hideTimer = null;
      this.activeTab = null;
      this.activeItem = null;
      this.modeOffs = [];
      this.originalOrder = this.sidebarItems.slice();

      this.panels.forEach((panel) => { hide(panel); panel.classList.remove(CLASSES.megaActive); });
      this.sidebarItems.forEach((li) => {
        li.setAttribute('role', 'button');
        li.setAttribute('tabindex', '-1');
        li.setAttribute('aria-expanded', 'false');
      });
      this.tabs.forEach((tab) => {
        var hasChildren = this.itemsForTab(tab).length > 0;
        tab.classList.toggle('tsc-megamenu-nav-item-has-children', hasChildren);
        tab.classList.toggle('tsc-megamenu-nav-item-no-children', !hasChildren);
        var link = tab.querySelector('a, button');
        if (link && hasChildren) {
          link.setAttribute('aria-haspopup', 'true');
          link.setAttribute('aria-expanded', 'false');
        }
      });

      this.setScrollbarWidthCheck = this.setScrollbarWidthCheck.bind(this);
      this.onResize = debounce(this.setScrollbarWidthCheck, 100);
      this.setScrollbarWidthCheck();
      this.on(window, 'resize', this.onResize, { passive: true });

      // Delegated click tracking for product + quick links (works for pointer and keyboard).
      this.on(this.wrapper, 'click', (event) => this.onTrackedClick(event));

      if (this.isDesktop()) this.enable();
    }

    bindMode(el, evt, fn, opts) {
      var off = this.on(el, evt, fn, opts);
      this.modeOffs.push(off);
      return off;
    }

    tabKey(tab) { return normalizeKey(tab.getAttribute('data-menu')); }

    /** Sidebar items belonging to a tab: by the tab's data-items list, else by each li's data-tab list. */
    itemsForTab(tab) {
      var key = this.tabKey(tab);
      var wanted = splitList(tab.getAttribute('data-items')).map(normalizeKey);
      var items = this.originalOrder.filter(function (li) {
        var item = normalizeKey(li.getAttribute('data-item'));
        if (wanted.length) return wanted.indexOf(item) !== -1;
        var tabsOfItem = splitList(li.getAttribute('data-tab')).map(normalizeKey);
        return tabsOfItem.indexOf(key) !== -1 || tabsOfItem.indexOf('*') !== -1 || tabsOfItem.indexOf('all') !== -1;
      });
      if (wanted.length) {
        items.sort(function (a, b) {
          return wanted.indexOf(normalizeKey(a.getAttribute('data-item'))) - wanted.indexOf(normalizeKey(b.getAttribute('data-item')));
        });
      }
      // Port of the legacy "first position in category" rule.
      var firstIdx = items.findIndex(function (li) {
        var first = normalizeKey(li.getAttribute('data-first-position-in-category') || li.getAttribute('data-first'));
        return first && first === key;
      });
      if (firstIdx > 0) items.unshift(items.splice(firstIdx, 1)[0]);
      return items;
    }

    panelFor(li) {
      var key = normalizeKey(li.getAttribute('data-item'));
      return this.panels.find(function (p) { return normalizeKey(p.getAttribute('data-panel')) === key; }) || null;
    }

    enable() {
      if (this.enabled) return;
      this.enabled = true;
      this.tabs.forEach((tab) => {
        var link = tab.querySelector('a, button') || tab;
        var hasChildren = tab.classList.contains('tsc-megamenu-nav-item-has-children');
        this.bindMode(tab, 'mouseenter', () => {
          this.cancelHide();
          hasChildren ? this.openTab(tab) : this.close();
        });
        this.bindMode(tab, 'mouseleave', () => this.startHideTimer());
        this.bindMode(link, 'focus', () => { this.cancelHide(); if (hasChildren) this.openTab(tab); });
        this.bindMode(tab, 'click', (event) => {
          var categoryName = cleanText(link.textContent);
          this.track('comfort_selected', legacyParams(['', categoryName, '', '', '', ''], { category: categoryName }));
          if (hasChildren) {
            event.preventDefault();
            if (this.activeTab === tab && this.mega.classList.contains(CLASSES.megaActive) && this.root.isTouch) this.close();
            else this.openTab(tab);
          }
        });
        this.bindMode(link, 'keydown', (event) => {
          if (!hasChildren) return;
          if (event.key === 'ArrowDown' || event.key === 'Down' || (isActivationKey(event) && link.tagName !== 'A')) {
            event.preventDefault();
            this.openTab(tab);
            var first = this.visibleSidebarItems()[0];
            if (first) { try { first.focus(); } catch (e) { /* noop */ } }
          } else if (event.key === 'Escape' || event.key === 'Esc') {
            if (!this.mega.classList.contains(CLASSES.megaActive)) return;
            event.preventDefault();
            event.stopPropagation();
            this.close();
          }
        });
      });

      this.bindMode(this.mega, 'mouseenter', () => this.cancelHide());
      this.bindMode(this.mega, 'mouseleave', () => this.startHideTimer());
      this.bindMode(this.wrapper, 'focusout', (event) => {
        if (event.relatedTarget && this.wrapper.contains(event.relatedTarget)) return;
        this.startHideTimer();
      });
      this.bindMode(this.wrapper, 'keydown', (event) => {
        if ((event.key === 'Escape' || event.key === 'Esc') && this.mega.classList.contains(CLASSES.megaActive)) {
          event.preventDefault();
          event.stopPropagation();
          var tabLink = this.activeTab ? (this.activeTab.querySelector('a, button') || this.activeTab) : null;
          this.close();
          if (tabLink) { try { tabLink.focus(); } catch (e) { /* noop */ } }
        }
      });

      if (this.sidebarList) {
        this.bindMode(this.sidebarList, 'mouseover', (event) => {
          var li = closestWithin(event.target, 'li[data-item]', this.sidebarList);
          if (li && li !== this.activeItem) this.openItem(li);
        });
        this.bindMode(this.sidebarList, 'click', (event) => {
          var li = closestWithin(event.target, 'li[data-item]', this.sidebarList);
          if (!li) return;
          this.openItem(li);
          this.trackSidebar(li);
        });
        this.bindMode(this.sidebarList, 'keydown', (event) => {
          var li = closestWithin(event.target, 'li[data-item]', this.sidebarList);
          if (!li) return;
          var visible = this.visibleSidebarItems();
          var idx = visible.indexOf(li);
          var next = null;
          if (event.key === 'ArrowDown' || event.key === 'Down') next = visible[(idx + 1) % visible.length];
          else if (event.key === 'ArrowUp' || event.key === 'Up') next = visible[(idx - 1 + visible.length) % visible.length];
          else if (event.key === 'Home') next = visible[0];
          else if (event.key === 'End') next = visible[visible.length - 1];
          else if (isActivationKey(event) || event.key === (this.root.isRtl ? 'ArrowLeft' : 'ArrowRight')) {
            event.preventDefault();
            this.openItem(li);
            this.trackSidebar(li);
            var first = this.activePanel ? getFocusable(this.activePanel)[0] : null;
            if (first) { try { first.focus(); } catch (e) { /* noop */ } }
            return;
          }
          if (next) {
            event.preventDefault();
            this.openItem(next);
            try { next.focus(); } catch (e) { /* noop */ }
          }
        });
      }

      this.bindMode(document, 'click', (event) => {
        if (!this.mega.classList.contains(CLASSES.megaActive)) return;
        if (this.wrapper.contains(event.target)) return;
        this.close();
      });
    }

    disable() {
      if (!this.enabled) return;
      this.enabled = false;
      this.close(true);
      this.modeOffs.forEach(function (off) { try { off(); } catch (e) { /* noop */ } });
      this.modeOffs = [];
    }

    visibleSidebarItems() {
      return this.sidebarItems.filter(isVisible);
    }

    openTab(tab) {
      if (!this.enabled || !tab) return;
      var items = this.itemsForTab(tab);
      if (!items.length) { this.close(); return; }
      this.root.closeAll('mega');
      if (this.activeTab !== tab) {
        this.activeTab = tab;
        this.tabs.forEach((t) => {
          var active = t === tab;
          t.classList.toggle(CLASSES.megaTabActive, active);
          var link = t.querySelector('a, button');
          if (link && link.hasAttribute('aria-haspopup')) link.setAttribute('aria-expanded', active ? 'true' : 'false');
        });
        this.buildSidebar(items);
        this.openItem(items[0]);
      }
      this.mega.classList.add(CLASSES.megaActive);
      this.root.classList.add(CLASSES.megaOpen);
      this.root.showOverlay('mega');
      this.setScrollbarWidthCheck();
    }

    /** Reorders + shows only the sidebar entries for the active tab (pre-rendered, toggled with hidden). */
    buildSidebar(items) {
      if (!this.sidebarList) return;
      this.sidebarItems.forEach((li) => {
        var visible = items.indexOf(li) !== -1;
        show(li, visible);
        li.setAttribute('tabindex', visible ? '0' : '-1');
      });
      items.forEach((li) => { if (li.parentElement === this.sidebarList) this.sidebarList.appendChild(li); });
      this.originalOrder.forEach((li) => { if (items.indexOf(li) === -1 && li.parentElement === this.sidebarList) this.sidebarList.appendChild(li); });
    }

    openItem(li) {
      if (!li) return;
      this.sidebarItems.forEach((x) => {
        var active = x === li;
        x.classList.toggle(CLASSES.megaActive, active);
        x.setAttribute('aria-expanded', active ? 'true' : 'false');
      });
      this.activeItem = li;
      var panel = this.panelFor(li);
      this.panels.forEach((p) => {
        var active = p === panel;
        show(p, active);
        p.classList.toggle(CLASSES.megaActive, active);
      });
      this.activePanel = panel;
      var titleEl = this.wrapper.querySelector('.tsc-megamenu-title, #TscMegaMenuTitle');
      if (titleEl) titleEl.textContent = cleanText(li.getAttribute('data-item'));
    }

    trackSidebar(li) {
      var now = Date.now();
      var last = toNumber(li.getAttribute('data-ga-last-tracked'), 0);
      if (now - last < TRACK_THROTTLE_MS) return;
      li.setAttribute('data-ga-last-tracked', String(now));
      var name = cleanText(li.getAttribute('data-item') || li.textContent);
      this.track('Menu Bar', legacyParams(['', name, '', '', '', ''], { category: name }));
    }

    onTrackedClick(event) {
      var productLink = closestWithin(event.target, '.desktop-mega-product-link', this.wrapper);
      if (productLink) {
        var menuText = cleanText(productLink.getAttribute('data-menu-text') || productLink.textContent);
        var productTitle = cleanText(productLink.getAttribute('data-product-title') || menuText);
        this.track('Menu Product', legacyParams(['', menuText, '', '', productTitle, ''], { menu_text: menuText, product_title: productTitle }));
        return;
      }
      var quickLink = closestWithin(event.target, '.tsc-megamenu-links a', this.wrapper);
      if (quickLink) {
        var text = cleanText(quickLink.textContent);
        this.track('Menu Product', legacyParams(['', text, '', '', text, ''], { menu_text: text, product_title: text }));
      }
    }

    startHideTimer() {
      this.cancelHide();
      this.hideTimer = this.setTimeout(() => {
        this.hideTimer = null;
        try { if (this.mega.matches(':hover')) return; } catch (e) { /* noop */ }
        this.close();
      }, MEGA_HIDE_DELAY_MS);
    }

    cancelHide() {
      if (this.hideTimer !== null) { this.clearTimer(this.hideTimer); this.hideTimer = null; }
    }

    close() {
      this.cancelHide();
      if (!this.mega) return;
      this.mega.classList.remove(CLASSES.megaActive);
      this.root.classList.remove(CLASSES.megaOpen);
      this.tabs.forEach((t) => {
        t.classList.remove(CLASSES.megaTabActive);
        var link = t.querySelector('a, button');
        if (link && link.hasAttribute('aria-haspopup')) link.setAttribute('aria-expanded', 'false');
      });
      this.activeTab = null;
      this.root.hideOverlay('mega');
    }

    /** Port of the legacy setScrollbarWidthCheck: full-bleed panel minus the scrollbar. */
    setScrollbarWidthCheck() {
      if (!this.mega) return;
      var width = Math.max(0, window.innerWidth - document.documentElement.clientWidth);
      this.root.style.setProperty('--ss-header-scrollbar-width', width + 'px');
      this.mega.style.setProperty('width', 'calc(100vw - ' + width + 'px)');
    }

    closeAll(reason) {
      if (reason === 'mega') return;
      this.close();
    }

    onBreakpoint(desktop) {
      desktop ? this.enable() : this.disable();
    }

    destroy() {
      this.disable();
      if (this.mega) this.mega.style.removeProperty('width');
      this.root.style.removeProperty('--ss-header-scrollbar-width');
      super.destroy();
    }
  }

  /* ------------------------------------------------------------------ */
  /* 5. Banner slider (scroll-snap + autoplay + dots)                    */
  /* ------------------------------------------------------------------ */

  class SsHeaderBannerSlider extends SsHeaderModule {
    init() {
      this.slider = this.$('[data-ss-banner-slider]') || this.$('.fs-banner-slider-prnt');
      if (!this.slider) return;
      this.slides = Array.from(this.slider.querySelectorAll('.fs-banner-wrapper'));
      if (this.slides.length < 2) return;

      this.index = 0;
      this.autoplay = get(this.config, 'bannerSlider.autoplay', true) !== false;
      this.interval = Math.max(1500, toNumber(get(this.config, 'bannerSlider.interval', BANNER_INTERVAL_MS), BANNER_INTERVAL_MS));
      this.paused = { hover: false, touch: false, focus: false, hidden: document.hidden === true, offscreen: true };
      this.timer = null;

      this.slider.setAttribute('aria-roledescription', this.t('carousel'));
      this.slider.setAttribute('aria-label', this.t('bannerSlider'));
      this.slides.forEach((slide, i) => {
        slide.setAttribute('aria-roledescription', this.t('slide'));
        slide.setAttribute('aria-label', this.t('slideStatus', { index: i + 1, total: this.slides.length }));
      });

      this.buildDots();
      this.buildStatus();

      var onScroll = () => {
        if (this.scrollRaf) return;
        this.scrollRaf = requestAnimationFrame(() => {
          this.scrollRaf = null;
          this.syncFromScroll();
        });
      };
      this.on(this.slider, 'scroll', onScroll, { passive: true });

      this.on(this.slider, 'pointerenter', () => this.setPaused('hover', true));
      this.on(this.slider, 'pointerleave', () => this.setPaused('hover', false));
      this.on(this.slider, 'touchstart', () => this.setPaused('touch', true), { passive: true });
      this.on(this.slider, 'touchend', () => { this.setTimeout(() => this.setPaused('touch', false), 1500); }, { passive: true });
      this.on(this.slider, 'touchcancel', () => this.setPaused('touch', false), { passive: true });
      this.on(this.slider, 'focusin', () => this.setPaused('focus', true));
      this.on(this.slider, 'focusout', (event) => { if (!event.relatedTarget || !this.slider.contains(event.relatedTarget)) this.setPaused('focus', false); });
      this.on(document, 'visibilitychange', () => this.setPaused('hidden', document.hidden === true));

      this.on(this.slider, 'click', (event) => {
        var slide = closestWithin(event.target, '.fs-banner-wrapper', this.slider);
        if (!slide) return;
        this.track('hamburger_banner_click', legacyParams(['HB_banner', slide.getAttribute('data-num') || String(this.slides.indexOf(slide) + 1)], { position: this.slides.indexOf(slide) + 1 }));
      });

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => this.setPaused('offscreen', !entry.isIntersecting));
        }, { threshold: 0.25 });
        this.observer.observe(this.slider);
      } else {
        this.paused.offscreen = false;
        this.updateAutoplay();
      }

      this.on(this.root, 'ss-header:menu', (event) => {
        this.updateAutoplay();
        if (event.detail && event.detail.open) {
          this.setTimeout(() => this.syncFromScroll(), 50);
        }
      });
    }

    buildDots() {
      var existing = this.slider.parentElement ? this.slider.parentElement.querySelector('.fs-banner-dots[data-ss-generated]') : null;
      if (existing) existing.remove();
      this.dotsEl = document.createElement('div');
      this.dotsEl.className = 'fs-banner-dots';
      this.dotsEl.setAttribute('data-ss-generated', '');
      this.dotsEl.setAttribute('role', 'group');
      this.dotsEl.setAttribute('aria-label', this.t('bannerDots'));
      this.dots = this.slides.map((slide, i) => {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'fs-banner-dot';
        btn.setAttribute('aria-label', this.t('slideGoTo', { index: i + 1, total: this.slides.length }));
        btn.setAttribute('data-index', String(i));
        this.dotsEl.appendChild(btn);
        return btn;
      });
      this.on(this.dotsEl, 'click', (event) => {
        var btn = closestWithin(event.target, '.fs-banner-dot', this.dotsEl);
        if (!btn) return;
        this.go(toNumber(btn.getAttribute('data-index'), 0), true);
        this.restartAutoplay();
      });
      this.on(this.dotsEl, 'keydown', (event) => {
        var dir = 0;
        if (event.key === 'ArrowRight' || event.key === 'Right') dir = 1;
        else if (event.key === 'ArrowLeft' || event.key === 'Left') dir = -1;
        if (!dir) return;
        if (this.root.isRtl) dir = -dir;
        event.preventDefault();
        var next = (this.index + dir + this.slides.length) % this.slides.length;
        this.go(next, true);
        try { this.dots[next].focus(); } catch (e) { /* noop */ }
        this.restartAutoplay();
      });
      this.slider.insertAdjacentElement('afterend', this.dotsEl);
      this.updateDots();
    }

    buildStatus() {
      this.statusEl = document.createElement('span');
      this.statusEl.className = CLASSES.srOnly;
      this.statusEl.setAttribute('aria-live', 'polite');
      this.statusEl.setAttribute('aria-atomic', 'true');
      this.statusEl.setAttribute('data-ss-generated', '');
      this.dotsEl.insertAdjacentElement('afterend', this.statusEl);
      this.updateStatus();
    }

    updateDots() {
      if (!this.dots) return;
      this.dots.forEach((dot, i) => {
        var active = i === this.index;
        dot.classList.toggle('is-active', active);
        if (active) dot.setAttribute('aria-current', 'true'); else dot.removeAttribute('aria-current');
        dot.setAttribute('tabindex', active ? '0' : '-1');
      });
    }

    updateStatus() {
      if (this.statusEl) this.statusEl.textContent = this.t('slideStatus', { index: this.index + 1, total: this.slides.length });
    }

    slideOffset(slide) {
      var sliderRect = this.slider.getBoundingClientRect();
      var rect = slide.getBoundingClientRect();
      return this.root.isRtl ? (rect.right - sliderRect.right) + this.slider.scrollLeft : (rect.left - sliderRect.left) + this.slider.scrollLeft;
    }

    syncFromScroll() {
      var best = 0, bestDist = Infinity;
      var sliderRect = this.slider.getBoundingClientRect();
      var center = this.root.isRtl ? sliderRect.right - sliderRect.width / 2 : sliderRect.left + sliderRect.width / 2;
      this.slides.forEach((slide, i) => {
        var r = slide.getBoundingClientRect();
        var dist = Math.abs((r.left + r.width / 2) - center);
        if (dist < bestDist) { bestDist = dist; best = i; }
      });
      if (best !== this.index) {
        this.index = best;
        this.updateDots();
        this.updateStatus();
      }
    }

    go(index, userInitiated) {
      var total = this.slides.length;
      var next = ((index % total) + total) % total;
      var slide = this.slides[next];
      if (!slide) return;
      var behavior = prefersReducedMotion() ? 'auto' : 'smooth';
      try {
        this.slider.scrollTo({ left: this.slideOffset(slide), behavior: behavior });
      } catch (e) {
        this.slider.scrollLeft = this.slideOffset(slide);
      }
      this.index = next;
      this.updateDots();
      if (userInitiated) this.updateStatus();
    }

    setPaused(key, value) {
      this.paused[key] = !!value;
      this.updateAutoplay();
    }

    isPaused() {
      var p = this.paused;
      return p.hover || p.touch || p.focus || p.hidden || p.offscreen || prefersReducedMotion() || !this.root.classList.contains(CLASSES.menuOpen);
    }

    updateAutoplay() {
      if (!this.autoplay) return;
      var shouldRun = !this.isPaused();
      if (shouldRun && this.timer === null) {
        this.timer = this.setInterval(() => {
          if (this.isPaused()) { this.updateAutoplay(); return; }
          this.go(this.index + 1, false);
        }, this.interval);
      } else if (!shouldRun && this.timer !== null) {
        this.clearTimer(this.timer);
        this.timer = null;
      }
    }

    restartAutoplay() {
      if (this.timer !== null) { this.clearTimer(this.timer); this.timer = null; }
      this.updateAutoplay();
    }

    destroy() {
      if (this.observer) { try { this.observer.disconnect(); } catch (e) { /* noop */ } this.observer = null; }
      if (this.scrollRaf) cancelAnimationFrame(this.scrollRaf);
      if (this.dotsEl) this.dotsEl.remove();
      if (this.statusEl) this.statusEl.remove();
      super.destroy();
    }
  }

  /* ------------------------------------------------------------------ */
  /* 6. Sticky header (mirrors the theme's StickyHeader logic)           */
  /* ------------------------------------------------------------------ */

  class SsHeaderSticky extends SsHeaderModule {
    init() {
      this.headerEl = this.header;
      if (!this.headerEl) return;
      // Config wins; the data-sticky / data-sticky-scroll-up attributes are the fallback.
      var enabledCfg = get(this.config, 'sticky.enabled', null);
      this.enabled = enabledCfg === null ? this.root.getAttribute('data-sticky') === 'true' : enabledCfg === true;
      var scrollUpCfg = get(this.config, 'sticky.showOnScrollUp', null);
      this.showOnScrollUp = scrollUpCfg === null ? this.root.getAttribute('data-sticky-scroll-up') === 'true' : scrollUpCfg === true;
      this.offset = Math.max(0, toNumber(get(this.config, 'sticky.offset', 0), 0));
      this.currentScrollTop = window.pageYOffset || document.documentElement.scrollTop || 0;
      this.lastCloseScrollTop = this.currentScrollTop;
      this.isAtTop = this.currentScrollTop <= 0;
      this.headerBounds = null;
      this.preventReveal = false;
      // Transparent header: solid as soon as the page is scrolled (CSS keys off .is-scrolled).
      this.transparent = this.root.classList.contains('ss-header--transparent');
      this.updateScrolled(this.currentScrollTop);

      this.measure = this.measure.bind(this);
      this.measure();
      this.onResize = debounce(() => { this.measure(); this.measureBounds(); }, 100);
      this.on(window, 'resize', this.onResize, { passive: true });
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(debounce(this.measure, 50));
        this.resizeObserver.observe(this.headerEl);
      }
      if (document.fonts && document.fonts.ready && typeof document.fonts.ready.then === 'function') {
        document.fonts.ready.then(() => { if (this.root.isConnected) this.measure(); }).catch(function () { /* noop */ });
      }
      this.on(window, 'load', this.measure);

      this.measureBounds();
      this.onScroll = this.onScroll.bind(this);
      this.on(window, 'scroll', this.onScroll, { passive: true });
      this.on(this.root, 'preventHeaderReveal', () => { this.preventReveal = true; });
    }

    updateScrolled(scrollTop) {
      if (!this.transparent) return;
      var scrolled = scrollTop > SCROLLED_PX;
      if (scrolled !== this.root.classList.contains(CLASSES.scrolled)) this.root.classList.toggle(CLASSES.scrolled, scrolled);
    }

    /** Publishes the header height for CSS (spacer, anchors, drawers) like the theme does. */
    measure() {
      if (!this.headerEl) return;
      var height = Math.round(this.headerEl.getBoundingClientRect().height);
      if (!height) return;
      this.height = height;
      var rootStyle = document.documentElement.style;
      rootStyle.setProperty('--ss-header-offset', height + 'px');
      this.root.style.setProperty('--ss-header-height', height + 'px');
      var groups = Array.from(document.querySelectorAll('.shopify-section-group-header-group'));
      var total = groups.reduce(function (sum, el) { return sum + el.offsetHeight; }, 0);
      rootStyle.setProperty('--header-height', (total > 0 ? total : height) + 'px');
    }

    measureBounds() {
      if (!this.root.isConnected) return;
      var scrollTop = window.pageYOffset || document.documentElement.scrollTop || 0;
      var wasSticky = this.root.classList.contains(CLASSES.sticky);
      var rect = this.root.getBoundingClientRect();
      var top = scrollTop + rect.top;
      // When already fixed, the element's rect is not its document position; keep the previous bounds.
      if (wasSticky && this.headerBounds) return;
      this.headerBounds = { top: top, bottom: top + (rect.height || this.height || 0) };
    }

    onScroll() {
      var scrollTop = window.pageYOffset || document.documentElement.scrollTop || 0;
      this.updateScrolled(scrollTop);

      if (Math.abs(scrollTop - this.lastCloseScrollTop) > SCROLL_CLOSE_DELTA_PX) {
        this.lastCloseScrollTop = scrollTop;
        this.root.closeAll('scroll');
      }

      if (!this.enabled || !this.headerBounds) { this.currentScrollTop = scrollTop; return; }

      if (scrollTop <= 0) {
        this.reset();
        this.isAtTop = true;
        this.currentScrollTop = scrollTop;
        return;
      }
      if (this.isAtTop && scrollTop > 10) this.isAtTop = false;
      if (this.isAtTop) { this.currentScrollTop = scrollTop; return; }

      var threshold = this.headerBounds.bottom + this.offset;
      var delta = scrollTop - this.currentScrollTop;

      if (scrollTop <= this.headerBounds.top) {
        requestAnimationFrame(() => this.reset());
      } else if (scrollTop > threshold) {
        if (this.showOnScrollUp || !this.isDesktop()) {
          if (delta > 0) {
            requestAnimationFrame(() => this.hide());
          } else if (delta < 0) {
            if (!this.preventReveal) {
              requestAnimationFrame(() => this.reveal());
            } else {
              this.clearTimer(this.revealTimer);
              this.revealTimer = this.setTimeout(() => { this.preventReveal = false; }, 66);
              requestAnimationFrame(() => this.hide());
            }
          }
        } else {
          requestAnimationFrame(() => this.reveal());
        }
      } else if (this.root.classList.contains(CLASSES.sticky)) {
        // Between header bottom and threshold while already sticky: keep the bar visible.
        requestAnimationFrame(() => this.reveal());
      }

      this.currentScrollTop = scrollTop;
    }

    hide() {
      if (this.isAtTop) return;
      if (this.root.classList.contains(CLASSES.menuOpen) || this.root.classList.contains(CLASSES.searchOpen)) return;
      this.root.classList.add(CLASSES.sticky, CLASSES.hidden);
      document.body.classList.add(BODY_CLASSES.stickyActive);
    }

    reveal() {
      if (this.isAtTop) return;
      this.root.classList.add(CLASSES.sticky, 'animate');
      this.root.classList.remove(CLASSES.hidden);
      document.body.classList.add(BODY_CLASSES.stickyActive);
    }

    reset() {
      this.root.classList.remove(CLASSES.sticky, CLASSES.hidden, 'animate');
      document.body.classList.remove(BODY_CLASSES.stickyActive);
    }

    onBreakpoint() {
      this.setTimeout(() => { this.measure(); this.measureBounds(); }, 50);
    }

    destroy() {
      if (this.resizeObserver) { try { this.resizeObserver.disconnect(); } catch (e) { /* noop */ } this.resizeObserver = null; }
      this.reset();
      this.root.style.removeProperty('--ss-header-height');
      super.destroy();
    }
  }

  /* ------------------------------------------------------------------ */
  /* 7. Search (modal open/close + rotating placeholder words)           */
  /* ------------------------------------------------------------------ */

  class SsHeaderSearch extends SsHeaderModule {
    init() {
      this.toggle = this.$('[data-ss-search-toggle]') || this.$('.header-search-icon');
      this.modal = this.$('[data-ss-search]') || this.$('.search-modal');
      if (!this.toggle || !this.modal) return;
      this.content = this.modal.querySelector('.search-modal__content') || this.modal;
      this.input = this.modal.querySelector('input[type="search"], input.search__input');
      this.cycleInner = this.modal.querySelector('.cycle-inner');
      this.cycleWrapper = this.modal.querySelector('.cycle-wrapper');
      this.isOpen = false;
      this.cycleTimer = null;
      this.cycleIndex = 0;

      if (!this.modal.id) this.modal.id = 'SsHeaderSearch-' + (this.root.sectionId || 'default');
      this.toggle.setAttribute('aria-controls', this.modal.id);
      this.toggle.setAttribute('aria-expanded', 'false');
      this.toggle.setAttribute('aria-label', this.t('openSearch'));
      if (!this.modal.hasAttribute('role')) this.modal.setAttribute('role', 'dialog');
      if (!this.modal.hasAttribute('aria-modal')) this.modal.setAttribute('aria-modal', 'true');
      if (!this.modal.hasAttribute('aria-label')) this.modal.setAttribute('aria-label', this.t('searchLabel'));
      if (this.input && !this.input.hasAttribute('aria-label') && !this.input.id) this.input.setAttribute('aria-label', this.t('searchLabel'));
      hide(this.modal);

      this.words = this.resolveWords();
      this.prepareCycle();

      this.on(this.toggle, 'click', (event) => {
        event.preventDefault();
        this.track('search_icon', legacyParams(['search_icon']));
        this.toggleModal();
      });
      Array.from(this.modal.querySelectorAll('[data-ss-search-close]')).forEach((el) => {
        // Buttons get a label; the overlay <div> stays pointer-only (ESC covers keyboard users).
        if (el.matches('button, a')) el.setAttribute('aria-label', this.t('closeSearch'));
        this.on(el, 'click', (event) => { event.preventDefault(); this.close(); });
      });
      if (this.input) {
        this.on(this.input, 'input', () => this.updateCycleState());
        this.on(this.input, 'focus', () => this.updateCycleState());
        this.on(this.input, 'blur', () => this.updateCycleState());
      }
      // The theme's predictive-search element closes itself on its own close button: mirror the modal state.
      var themeClose = this.modal.querySelector('predictive-search button[type="button"]:not([data-ss-search-close])');
      if (themeClose) this.on(themeClose, 'click', () => this.close());
    }

    resolveWords() {
      var fromConfig = get(this.config, 'searchWords', null);
      var fromAttr = null;
      var attrHost = this.modal.hasAttribute('data-ss-search-words') ? this.modal : this.modal.querySelector('[data-ss-search-words]');
      if (attrHost) {
        var raw = attrHost.getAttribute('data-ss-search-words');
        fromAttr = parseJsonAttr(raw, null);
        if (!Array.isArray(fromAttr)) fromAttr = splitList(raw);
      }
      var list = Array.isArray(fromConfig) && fromConfig.length ? fromConfig : (Array.isArray(fromAttr) ? fromAttr : []);
      list = list.map(cleanText).filter(Boolean);
      if (!list.length && this.cycleInner) {
        list = Array.from(this.cycleInner.children).map(function (el) { return cleanText(el.textContent); }).filter(Boolean);
      }
      return list;
    }

    prepareCycle() {
      if (!this.cycleInner) return;
      if (this.cycleWrapper) this.cycleWrapper.setAttribute('aria-hidden', 'true');
      if (!this.words.length) return;
      this.cycleInner.textContent = '';
      // Words + a clone of the first so the loop can wrap without a visible jump.
      this.words.concat(this.words[0]).forEach((word) => {
        var span = document.createElement('span');
        span.textContent = word;
        this.cycleInner.appendChild(span);
      });
      this.cycleInner.style.transform = 'translateY(0)';
      this.cycleIndex = 0;
    }

    lineHeight() {
      var first = this.cycleInner ? this.cycleInner.firstElementChild : null;
      var h = (this.cycleWrapper && this.cycleWrapper.clientHeight) || (first && first.offsetHeight) || 0;
      return h || parseFloat(getComputedStyle(this.cycleInner).lineHeight) || 16;
    }

    shouldCycle() {
      return this.isOpen && this.words.length > 1 && !prefersReducedMotion() && this.input && !this.input.value;
    }

    updateCycleState() {
      this.shouldCycle() ? this.startCycle() : this.stopCycle();
    }

    startCycle() {
      if (!this.cycleInner || this.cycleTimer !== null) return;
      this.cycleInner.style.transition = '';
      this.cycleTimer = this.setInterval(() => this.cycleStep(), SEARCH_CYCLE_MS);
    }

    cycleStep() {
      if (!this.shouldCycle()) { this.stopCycle(); return; }
      var total = this.words.length;
      var h = this.lineHeight();
      this.cycleIndex += 1;
      this.cycleInner.style.transition = '';
      this.cycleInner.style.transform = 'translateY(' + (-this.cycleIndex * h) + 'px)';
      if (this.cycleIndex >= total) {
        // We're on the clone of word 0: snap back without transition after the animation ends.
        this.setTimeout(() => {
          if (!this.cycleInner) return;
          this.cycleInner.style.transition = 'none';
          this.cycleInner.style.transform = 'translateY(0)';
          this.cycleIndex = 0;
          // Force reflow so the next step animates again.
          void this.cycleInner.offsetHeight;
          this.cycleInner.style.transition = '';
        }, 480);
      }
    }

    stopCycle() {
      if (this.cycleTimer !== null) { this.clearTimer(this.cycleTimer); this.cycleTimer = null; }
      if (this.cycleInner && (this.input && this.input.value || prefersReducedMotion())) {
        this.cycleInner.style.transition = 'none';
        this.cycleInner.style.transform = 'translateY(0)';
        this.cycleIndex = 0;
      }
    }

    toggleModal() { this.isOpen ? this.close() : this.open(); }

    open() {
      if (this.isOpen) return;
      this.isOpen = true;
      this.root.closeAll('search');
      show(this.modal);
      this.modal.classList.add('is-open');
      this.root.classList.add(CLASSES.searchOpen);
      document.body.classList.add(BODY_CLASSES.searchOpen);
      this.toggle.setAttribute('aria-expanded', 'true');
      this.toggle.setAttribute('aria-label', this.t('closeSearch'));
      this.root.layers.push({
        container: this.content,
        initialFocus: this.input || undefined,
        returnFocus: this.toggle,
        onClose: () => this.close()
      });
      this.updateCycleState();
      this.root.dispatchEvent(new CustomEvent('ss-header:search', { detail: { open: true } }));
    }

    close(silent) {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.stopCycle();
      this.modal.classList.remove('is-open', 'searching');
      hide(this.modal);
      this.root.classList.remove(CLASSES.searchOpen);
      document.body.classList.remove(BODY_CLASSES.searchOpen, 'predictive-search--focus');
      this.toggle.setAttribute('aria-expanded', 'false');
      this.toggle.setAttribute('aria-label', this.t('openSearch'));
      var ps = this.modal.querySelector('predictive-search');
      if (ps && typeof ps.close === 'function') { try { ps.close(); } catch (e) { /* noop */ } }
      this.root.layers.remove(this.content, silent === true);
      this.root.dispatchEvent(new CustomEvent('ss-header:search', { detail: { open: false } }));
    }

    closeAll(reason) {
      if (reason === 'search' || reason === 'scroll') return;
      if (reason === 'hamburger' || reason === 'overlay' || reason === 'editor' || reason === 'breakpoint') this.close(reason === 'breakpoint');
    }

    destroy() {
      this.close(true);
      super.destroy();
    }
  }

  /* ------------------------------------------------------------------ */
  /* 8. Cart badge fallback                                              */
  /* ------------------------------------------------------------------ */

  class SsHeaderCart extends SsHeaderModule {
    init() {
      this.link = this.$('#cart-icon-bubble') || this.$('.header__icon--cart');
      if (!this.link) return;
      this.themeUpdatedAt = 0;
      var glyph = this.link.querySelector('.ss-header-cart-glyph');
      this.glyphHTML = glyph ? glyph.outerHTML : '';
      if ('MutationObserver' in window) {
        this.observer = new MutationObserver(() => {
          this.themeUpdatedAt = Date.now();
          this.restoreGlyph();
        });
        this.observer.observe(this.link, { childList: true, subtree: true, characterData: true });
      }
      this.bindDrawer();
      if (ssPubSubEvents() && ssPubSubEvents().cartUpdate) {
        this.unsubscribe = ssSubscribe(ssPubSubEvents().cartUpdate, (event) => this.onCartUpdate(event));
      }
      this.on(document, 'cart:refresh', (event) => this.onCartUpdate(get(event, 'detail', {})));
    }

    /* The link keeps href="/cart" (no-JS and no-drawer fallback); when the
       theme cart drawer is on the page (snippets/ss-header-cart-drawer) a
       plain click opens it instead. Modified clicks still open the page. */
    bindDrawer() {
      if (!this.link.hasAttribute('data-ss-cart-drawer')) return;
      this.link.setAttribute('aria-haspopup', 'dialog');
      this.link.setAttribute('aria-controls', 'mini-cart');
      this.link.setAttribute('aria-expanded', 'false');
      this.on(this.link, 'click', (event) => this.onLinkClick(event));
      var drawer = document.querySelector('cart-drawer');
      if (drawer) {
        // Fired by the theme drawer after it closes (overlay, close button, Escape).
        this.on(drawer, 'close', () => {
          this.link.setAttribute('aria-expanded', 'false');
          var active = document.activeElement;
          if (!active || active === document.body || drawer.contains(active)) {
            try { this.link.focus({ preventScroll: true }); } catch (e) { this.link.focus(); }
          }
        });
      }
    }

    onLinkClick(event) {
      if (event.defaultPrevented || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      var drawer = document.querySelector('cart-drawer');
      var details = drawer && drawer.querySelector('details');
      if (!details || typeof drawer.openMenuDrawer !== 'function') return; // no drawer: follow the link to /cart
      event.preventDefault();
      if (details.hasAttribute('open')) return;
      // Close any open header layer (menu, search) first.
      if (typeof this.root.closeAll === 'function') this.root.closeAll('cart');
      drawer.openMenuDrawer();
      this.link.setAttribute('aria-expanded', 'true');
      var panel = document.getElementById('mini-cart');
      if (!panel) return;
      // openMenuDrawer() sets [open] on the next tick; move focus once the panel is visible.
      this.setTimeout(() => {
        if (typeof window.trapFocus === 'function') {
          window.trapFocus(panel, panel);
        } else {
          try { panel.focus({ preventScroll: true }); } catch (e) { panel.focus(); }
        }
      }, 60);
    }

    /* MiniCart.renderContents() (after add to cart) swaps the link content for
       the stock `cart-icon-bubble` section, which uses the theme cart glyph.
       Put the SS glyph back so the icon does not change. */
    restoreGlyph() {
      if (!this.glyphHTML || !this.link || this.link.querySelector('.ss-header-cart-glyph')) return;
      Array.prototype.forEach.call(this.link.querySelectorAll('svg.icon-cart'), function (svg) { svg.remove(); });
      this.link.insertAdjacentHTML('afterbegin', this.glyphHTML);
    }

    onCartUpdate(event) {
      var count = get(event, 'cartData.item_count', null);
      if (count === null) count = get(event, 'cart.item_count', null);
      if (count === null) count = get(event, 'item_count', null);
      if (count === null) return;
      var publishedAt = Date.now();
      // Give the theme's section re-render a moment; if it replaced the icon markup, do nothing.
      this.setTimeout(() => {
        if (!this.link || !this.link.isConnected) return;
        if (this.themeUpdatedAt >= publishedAt) return;
        if (this.displayedCount() === toNumber(count, 0)) return;
        this.render(toNumber(count, 0));
      }, 400);
    }

    displayedCount() {
      var bubble = this.link.querySelector('.cart-count-bubble');
      if (!bubble || !isVisible(bubble)) return 0;
      var visibleText = bubble.querySelector('[aria-hidden="true"]');
      return toNumber(cleanText(visibleText ? visibleText.textContent : bubble.textContent).replace(/\D+/g, ''), 0);
    }

    render(count) {
      var bubble = this.link.querySelector('.cart-count-bubble');
      if (count <= 0) {
        if (bubble) hide(bubble);
        return;
      }
      if (!bubble) {
        bubble = document.createElement('div');
        bubble.className = 'cart-count-bubble ' + CLASSES.badge;
        this.link.appendChild(bubble);
      }
      var visible = bubble.querySelector('[aria-hidden="true"]');
      if (!visible) {
        visible = document.createElement('span');
        visible.setAttribute('aria-hidden', 'true');
        bubble.appendChild(visible);
      }
      var sr = bubble.querySelector('.visually-hidden');
      if (!sr) {
        sr = document.createElement('span');
        sr.className = 'visually-hidden';
        bubble.appendChild(sr);
      }
      visible.textContent = count > 99 ? '99+' : String(count);
      sr.textContent = this.t(count === 1 ? 'cartItemsOne' : 'cartItemsOther', { count: count });
      show(bubble);
      if (bubble.style.display === 'none') bubble.style.display = '';
      this.themeUpdatedAt = Date.now();
    }

    destroy() {
      if (this.observer) { try { this.observer.disconnect(); } catch (e) { /* noop */ } this.observer = null; }
      if (typeof this.unsubscribe === 'function') { try { this.unsubscribe(); } catch (e) { /* noop */ } this.unsubscribe = null; }
      super.destroy();
    }
  }

  /* ------------------------------------------------------------------ */
  /* 9. Declarative tracking: [data-ss-track]                            */
  /* ------------------------------------------------------------------ */

  class SsHeaderTracking extends SsHeaderModule {
    init() {
      this.on(this.root, 'click', (event) => {
        var el = closestWithin(event.target, '[data-ss-track]', this.root);
        if (!el) return;
        var eventName = cleanText(el.getAttribute('data-ss-track'));
        if (!eventName) return;
        var params = parseJsonAttr(el.getAttribute('data-ss-track-params'), null);
        var legacy = [];
        for (var i = 1; i <= 6; i++) legacy.push(el.getAttribute('data-ss-track-param' + i) || '');
        var hasLegacy = legacy.some(Boolean);
        if (!hasLegacy && isObject(params) && Array.isArray(params.legacy)) { legacy = params.legacy; hasLegacy = true; }
        var extra = isObject(params) && !Array.isArray(params) ? Object.assign({}, params) : {};
        delete extra.legacy;
        if (!extra.label) extra.label = cleanText(el.getAttribute('data-ss-track-label') || el.getAttribute('aria-label') || el.textContent).slice(0, 120);
        this.track(eventName, hasLegacy ? legacyParams(legacy, extra) : extra);
      });
    }
  }

  /* ------------------------------------------------------------------ */
  /* Root custom element                                                 */
  /* ------------------------------------------------------------------ */

  var MODULES = [
    ['tracking', SsHeaderTracking],
    ['sticky', SsHeaderSticky],
    ['levels', SsHeaderLevels],
    ['hamburger', SsHeaderHamburger],
    ['desktopNav', SsHeaderDesktopNav],
    ['megaMenu', SsHeaderMegaMenu],
    ['bannerSlider', SsHeaderBannerSlider],
    ['search', SsHeaderSearch],
    ['cart', SsHeaderCart]
  ];

  class SsHeader extends HTMLElement {
    constructor() {
      super();
      this._listeners = [];
      this._modules = [];
      this._initialized = false;
      this._overlayOwners = new Set();
    }

    connectedCallback() {
      if (this._initialized) return;
      var configEl = this.querySelector('script[data-ss-header-config]');
      var config = parseJsonScript(configEl, null);
      if (!config || !isObject(config)) {
        if (!SsHeader._warnedMissingConfig) {
          SsHeader._warnedMissingConfig = true;
          warn('Missing or invalid config JSON (script[data-ss-header-config]); using defaults.');
        }
        config = {};
      }
      this._initialized = true;
      this.config = config;
      this.sectionId = config.sectionId || this.dataset.sectionId || '';
      this.t = createTranslator(config.strings);
      this.track = createTracker(config);
      this.layers = createLayerStack();
      this.headerEl = this.querySelector('header.new-header-wrapper') || this.querySelector('header') || this;
      this.isRtl = document.documentElement.getAttribute('dir') === 'rtl';
      this.isTouch = get(window, 'theme.config.isTouch', null);
      if (this.isTouch === null) {
        try { this.isTouch = window.matchMedia('(hover: none)').matches; } catch (e) { this.isTouch = 'ontouchstart' in window; }
      }

      var desktopMin = toNumber(get(config, 'breakpoints.desktop', DESKTOP_MIN_WIDTH), DESKTOP_MIN_WIDTH);
      try {
        this.mql = window.matchMedia('(min-width: ' + desktopMin + 'px)');
      } catch (e) {
        this.mql = { matches: window.innerWidth >= desktopMin };
      }
      this._desktop = !!this.mql.matches;
      this.classList.toggle(CLASSES.desktop, this._desktop);
      this.classList.toggle(CLASSES.mobile, !this._desktop);
      this._onMqlChange = (e) => this.setDesktop(!!e.matches);
      if (typeof this.mql.addEventListener === 'function') this.mql.addEventListener('change', this._onMqlChange);
      else if (typeof this.mql.addListener === 'function') this.mql.addListener(this._onMqlChange);

      MODULES.forEach((entry) => {
        var name = entry[0], Ctor = entry[1];
        try {
          var instance = new Ctor(this);
          this[name] = instance;
          this._modules.push(instance);
          instance.init();
        } catch (e) {
          warn('module "' + name + '" failed to initialise', e);
        }
      });

      // Theme editor
      this.on(document, 'shopify:section:unload', (e) => {
        if (this.matchesSection(e)) this.destroy();
      });
      this.on(document, 'shopify:section:load', (e) => {
        if (this.matchesSection(e) && !this._initialized && this.isConnected) this.connectedCallback();
      });
      this.on(document, 'shopify:block:select', (e) => this.onBlockSelect(e));
      this.on(document, 'shopify:block:deselect', (e) => {
        if (!this.matchesSection(e)) return;
        if (this.megaMenu && this.megaMenu.wrapper && e.target && this.megaMenu.wrapper.contains(e.target)) this.megaMenu.close();
      });

      window.SsHeader = this;
      this.dispatchEvent(new CustomEvent('ss-header:ready', { bubbles: true, detail: { instance: this } }));
    }

    disconnectedCallback() { this.destroy(); }

    matchesSection(e) {
      var id = get(e, 'detail.sectionId', null);
      if (!id || !this.sectionId) return e && e.target && (e.target === this || this.contains(e.target) || (e.target.contains && e.target.contains(this)));
      return String(id) === String(this.sectionId);
    }

    /** Design mode: selecting a block inside the mobile panel / mega menu opens it so merchants can see it. */
    onBlockSelect(e) {
      if (!this.matchesSection(e) || !e.target) return;
      var target = e.target;
      var mobilePanel = this.hamburger && this.hamburger.panel;
      if (mobilePanel && mobilePanel.contains(target)) {
        if (!this._desktop && this.hamburger) {
          this.hamburger.open();
          var item = target.closest('.menu-item');
          if (item && this.levels && item.querySelector('.prnt-div-whole')) this.levels.openLevel(item);
          var group = target.closest('.submenu-item');
          if (group && this.levels) this.levels.setGroup(group, true);
        } else if (this._desktop && this.desktopNav) {
          var navItem = target.closest('.menu-item.custom-dropdown');
          if (navItem) this.desktopNav.open(navItem);
        }
        return;
      }
      if (this.megaMenu && this.megaMenu.wrapper && this.megaMenu.wrapper.contains(target) && this._desktop) {
        var tab = target.closest('.tsc-megamenu-nav-item[data-menu]');
        var panel = target.closest('.tsc-megamenu-panel[data-panel]');
        var li = target.closest('li[data-item]');
        if (tab) this.megaMenu.openTab(tab);
        else {
          var firstTab = this.megaMenu.tabs.find(function (t) { return t.classList.contains('tsc-megamenu-nav-item-has-children'); });
          if (firstTab) this.megaMenu.openTab(firstTab);
          if (li) this.megaMenu.openItem(li);
          else if (panel) {
            var match = this.megaMenu.sidebarItems.find(function (x) { return normalizeKey(x.getAttribute('data-item')) === normalizeKey(panel.getAttribute('data-panel')); });
            if (match) this.megaMenu.openItem(match);
          }
        }
        return;
      }
      if (this.search && this.search.modal && this.search.modal.contains(target)) this.search.open();
    }

    isDesktop() { return this._desktop; }

    setDesktop(desktop) {
      if (desktop === this._desktop) return;
      this._desktop = desktop;
      this.classList.toggle(CLASSES.desktop, desktop);
      this.classList.toggle(CLASSES.mobile, !desktop);
      this.closeAll('breakpoint');
      this._modules.forEach((m) => {
        try { m.onBreakpoint(desktop); } catch (e) { warn('onBreakpoint', e); }
      });
      this.dispatchEvent(new CustomEvent('ss-header:breakpoint', { detail: { desktop: desktop } }));
    }

    /** Asks every module to close its open UI; `reason` lets the caller stay open. */
    closeAll(reason) {
      this._modules.forEach((m) => {
        try { m.closeAll(reason || 'all'); } catch (e) { warn('closeAll', e); }
      });
    }

    /** Registers a listener and remembers it for cleanup. */
    on(el, evt, fn, opts) {
      if (!el || typeof el.addEventListener !== 'function') return function () {};
      el.addEventListener(evt, fn, opts);
      var record = { el: el, evt: evt, fn: fn, opts: opts };
      this._listeners.push(record);
      return () => {
        el.removeEventListener(evt, fn, opts);
        this._listeners = this._listeners.filter((r) => r !== record);
      };
    }

    /* Page overlay (shared by the mobile panel and the mega menu; lives on <body>). */
    getOverlay() {
      if (this._overlay && this._overlay.isConnected) return this._overlay;
      var overlay = document.createElement('div');
      overlay.className = CLASSES.overlay;
      overlay.setAttribute('data-ss-header-overlay', this.sectionId || '');
      overlay.setAttribute('aria-hidden', 'true');
      overlay.setAttribute('hidden', '');
      document.body.appendChild(overlay);
      this.on(overlay, 'click', () => this.closeAll('overlay'));
      this._overlay = overlay;
      return overlay;
    }

    showOverlay(owner) {
      this._overlayOwners.add(owner || 'default');
      var overlay = this.getOverlay();
      overlay.setAttribute('data-owner', Array.from(this._overlayOwners).join(' '));
      show(overlay);
      requestAnimationFrame(() => { if (this._overlayOwners.size) overlay.classList.add(CLASSES.overlayVisible); });
      document.body.classList.add(BODY_CLASSES.overlayOpen);
    }

    hideOverlay(owner) {
      this._overlayOwners.delete(owner || 'default');
      if (this._overlayOwners.size || !this._overlay) return;
      var overlay = this._overlay;
      overlay.classList.remove(CLASSES.overlayVisible);
      overlay.removeAttribute('data-owner');
      document.body.classList.remove(BODY_CLASSES.overlayOpen);
      if (prefersReducedMotion()) { hide(overlay); return; }
      var done = false;
      var finish = () => { if (done) return; done = true; if (!this._overlayOwners.size) hide(overlay); };
      overlay.addEventListener('transitionend', finish, { once: true });
      setTimeout(finish, 350);
    }

    destroy() {
      if (!this._initialized) return;
      this._initialized = false;
      this._modules.slice().reverse().forEach((m) => { try { m.destroy(); } catch (e) { warn('destroy', e); } });
      this._modules = [];
      MODULES.forEach((entry) => { delete this[entry[0]]; });
      if (this.mql && this._onMqlChange) {
        if (typeof this.mql.removeEventListener === 'function') this.mql.removeEventListener('change', this._onMqlChange);
        else if (typeof this.mql.removeListener === 'function') this.mql.removeListener(this._onMqlChange);
      }
      this._listeners.forEach((r) => { try { r.el.removeEventListener(r.evt, r.fn, r.opts); } catch (e) { /* noop */ } });
      this._listeners = [];
      if (this.layers) this.layers.clear();
      this._overlayOwners.clear();
      if (this._overlay) { try { this._overlay.remove(); } catch (e) { /* noop */ } this._overlay = null; }
      Object.keys(BODY_CLASSES).forEach((k) => document.body.classList.remove(BODY_CLASSES[k]));
      this.classList.remove(CLASSES.menuOpen, CLASSES.levelOpen, CLASSES.dropdownOpen, CLASSES.megaOpen, CLASSES.searchOpen, CLASSES.sticky, CLASSES.scrolled, CLASSES.hidden, 'animate');
      if (window.SsHeader === this) delete window.SsHeader;
    }
  }

  SsHeader._warnedMissingConfig = false;
  customElements.define('ss-header', SsHeader);
})();
